import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useTheme
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import WarningIcon from '@mui/icons-material/Warning';
import api from '../../shared/services/api';
import { useTranslation } from '../../store/languageStore';
import SectionHeader from './SectionHeader';

interface UploadedDocument {
  id: string;
  filename: string;
  document_type: string;
  status: 'uploading' | 'extracting' | 'completed' | 'error';
  extracted_data?: any;
  confidence?: number;
  error?: string;
}

interface AIDocumentUploadTabProps {
  declarationId: string;
  onAutoFill: (data: any) => void;
}

const documentTypes = [
  { value: 'invoice', label: 'Commercial Invoice' },
  { value: 'packing_list', label: 'Packing List' },
  { value: 'bill_of_lading', label: 'Bill of Lading' },
  { value: 'certificate_of_origin', label: 'Certificate of Origin' }
];

const AIDocumentUploadTab: React.FC<AIDocumentUploadTabProps> = ({
  declarationId,
  onAutoFill
}) => {
  const { t, tString } = useTranslation();
  const theme = useTheme();
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, docType: string) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const docId = Date.now().toString();

    // Add to list as uploading
    const newDoc: UploadedDocument = {
      id: docId,
      filename: file.name,
      document_type: docType,
      status: 'uploading'
    };

    setDocuments(prev => [...prev, newDoc]);
    setUploading(true);

    try {
      // Upload and extract
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);

      // Update status to extracting
      setDocuments(prev =>
        prev.map(d => d.id === docId ? { ...d, status: 'extracting' as const } : d)
      );

      const response = await api.post('/api/v1/customs-ai/extract-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update with extracted data
      setDocuments(prev =>
        prev.map(d =>
          d.id === docId
            ? {
                ...d,
                status: 'completed' as const,
                extracted_data: response.data.extracted_data,
                confidence: response.data.confidence
              }
            : d
        )
      );
    } catch (error: any) {
      setDocuments(prev =>
        prev.map(d =>
          d.id === docId
            ? {
                ...d,
                status: 'error' as const,
                error: error.response?.data?.detail || 'Extraction failed'
              }
            : d
        )
      );
    } finally {
      setUploading(false);
    }
  };

  const handleAutoFill = async () => {
    const completedDocs = documents.filter(d => d.status === 'completed');
    if (completedDocs.length === 0) return;

    setAutoFilling(true);

    try {
      const documentsData = completedDocs.map(d => ({
        document_type: d.document_type,
        extracted_data: d.extracted_data,
        confidence: d.confidence
      }));

      const response = await api.post(
        `/api/v1/customs-ai/auto-fill/${declarationId}`,
        documentsData
      );

      if (response.data.success) {
        onAutoFill(response.data);
      }
    } catch (error: any) {
      console.error('Auto-fill failed:', error);
    } finally {
      setAutoFilling(false);
    }
  };

  const handleDelete = (docId: string) => {
    setDocuments(prev => prev.filter(d => d.id !== docId));
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  const completedCount = documents.filter(d => d.status === 'completed').length;
  const averageConfidence = completedCount > 0
    ? documents
        .filter(d => d.status === 'completed')
        .reduce((sum, d) => sum + (d.confidence || 0), 0) / completedCount
    : 0;

  return (
    <Box>
      <SectionHeader
        title={t('customsDeclaration.aiUpload.title') as string}
        subtitle={t('customsDeclaration.aiUpload.subtitle') as string}
        icon={<SmartToyIcon color="primary" sx={{ fontSize: 32 }} />}
      />

      {/* Stats Cards */}
      {documents.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Documents Processed
                </Typography>
                <Typography variant="h4">{completedCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Average Confidence
                </Typography>
                <Typography variant="h4">
                  {(averageConfidence * 100).toFixed(0)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Ready to Auto-Fill
                </Typography>
                <Typography variant="h4">
                  {completedCount > 0 ? 'Yes' : 'No'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Upload Buttons */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Upload Documents
        </Typography>
        <Grid container spacing={2}>
          {documentTypes.map(type => (
            <Grid item xs={12} sm={6} md={3} key={type.value}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<UploadFileIcon />}
                disabled={uploading}
              >
                {type.label}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload(e, type.value)}
                />
              </Button>
            </Grid>
          ))}
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Supported: PDF, JPG, PNG (Max 10MB)
        </Typography>
      </Paper>

      {/* Documents List */}
      {documents.length > 0 ? (
        <>
          <List>
            {documents.map(doc => (
              <Paper key={doc.id} variant="outlined" sx={{ mb: 2 }}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1">{doc.filename}</Typography>
                        <Chip
                          label={documentTypes.find(t => t.value === doc.document_type)?.label}
                          size="small"
                          variant="outlined"
                        />
                        {doc.status === 'completed' && (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label={`${(doc.confidence! * 100).toFixed(0)}% confidence`}
                            size="small"
                            color={getConfidenceColor(doc.confidence!)}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        {doc.status === 'uploading' && (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Uploading...
                            </Typography>
                            <LinearProgress sx={{ mt: 1 }} />
                          </>
                        )}
                        {doc.status === 'extracting' && (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              AI extracting data...
                            </Typography>
                            <LinearProgress sx={{ mt: 1 }} />
                          </>
                        )}
                        {doc.status === 'completed' && (
                          <Typography variant="body2" color="success">
                            ✓ Data extracted successfully
                          </Typography>
                        )}
                        {doc.status === 'error' && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            {doc.error}
                          </Alert>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleDelete(doc.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Paper>
            ))}
          </List>

          {/* Auto-Fill Button */}
          {completedCount > 0 && (
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: theme.palette.primary.light }}>
              <Typography variant="h6" gutterBottom>
                Ready to Auto-Fill Declaration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {completedCount} document{completedCount > 1 ? 's' : ''} processed with{' '}
                {(averageConfidence * 100).toFixed(0)}% average confidence
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={autoFilling ? null : <AutoFixHighIcon />}
                onClick={handleAutoFill}
                disabled={autoFilling}
              >
                {autoFilling ? 'Auto-Filling...' : 'Auto-Fill All Tabs with AI'}
              </Button>
              {averageConfidence < 0.7 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Confidence is below 70%. Please review AI-filled data carefully.
                  </Typography>
                </Alert>
              )}
            </Paper>
          )}
        </>
      ) : (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <UploadFileIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Documents Uploaded Yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload invoices, packing lists, or other documents to auto-fill the declaration
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AIDocumentUploadTab;
